import React, { useState, useCallback } from "react";
import { Modal, Table, Typography, Button, Tag, Empty, Tooltip } from "antd";
import { SendOutlined, CloseOutlined } from "@ant-design/icons";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  PRIMARY_600,
  PRIMARY_800,
  GRAY_50,
  GRAY_100,
  GRAY_200,
  GRAY_500,
  GRAY_600,
  GRAY_700,
  GRAY_900,
  WHITE,
  SUCCESS_700,
  WARNING_700,
  ERROR_500
} from "../../constant/colors";
import { fontWeights, sizes } from "../../constant/styles";
import { ACTION_TYPES } from "./constants";

const { Text } = Typography;

const StyledModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 12px;
    overflow: hidden;
  }

  .ant-modal-header {
    padding: 16px 24px;
    border-bottom: 1px solid ${GRAY_200};
    margin-bottom: 0;
  }

  .ant-modal-title {
    font-size: ${sizes.large};
    font-weight: ${fontWeights.semibold};
    color: ${GRAY_900};
  }

  .ant-modal-body {
    padding: 16px 24px;
    max-height: 60vh;
    overflow-y: auto;
  }

  .ant-modal-footer {
    padding: 16px 24px;
    border-top: 1px solid ${GRAY_200};
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .ant-modal-close {
    top: 16px;
    right: 16px;
  }
`;

const ModalSubtitle = styled(Text)`
  font-size: ${sizes.medium};
  color: ${GRAY_500};
  display: block;
  margin-top: 2px;
`;

const StyledTable = styled(Table)`
  && {
    .ant-table {
      border: 1px solid ${GRAY_200};
      border-radius: 8px;
    }

    .ant-table-thead > tr > th {
      background: ${GRAY_50};
      font-weight: ${fontWeights.semibold};
      font-size: ${sizes.small};
      color: ${GRAY_900};
      text-transform: uppercase;
      padding: 10px 12px;
      border-bottom: 1px solid ${GRAY_200};
    }

    .ant-table-tbody > tr > td {
      padding: 10px 12px;
      font-size: ${sizes.medium};
      color: ${GRAY_900};
      border-bottom: 1px solid ${GRAY_100};
    }

    .ant-table-tbody > tr:hover > td {
      background: ${GRAY_50};
    }

    .ant-checkbox-checked .ant-checkbox-inner {
      background-color: ${PRIMARY_600};
      border-color: ${PRIMARY_600};
    }
  }
`;

const PatientName = styled.div`
  display: flex;
  flex-direction: column;
`;

const PatientLink = styled(Link)`
  font-weight: ${fontWeights.semibold};
  color: ${GRAY_900};
  font-size: ${sizes.medium};
  text-decoration: none;

  &:hover {
    color: ${PRIMARY_600};
    text-decoration: underline;
  }
`;

const PatientId = styled(Text)`
  font-size: ${sizes.small};
  color: ${GRAY_500};
`;

const DrugTag = styled(Tag)`
  && {
    background: #eff8ff;
    color: ${PRIMARY_600};
    border: 1px solid #84caff;
    border-radius: 4px;
    font-size: ${sizes.small};
  }
`;

const UrgencyBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: ${sizes.small};
  font-weight: ${fontWeights.semibold};
  background: ${(props) => props.bgColor};
  color: ${(props) => props.textColor};
`;

const FooterInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${GRAY_600};
  font-size: ${sizes.medium};
`;

const FooterActions = styled.div`
  display: flex;
  gap: 8px;
`;

const CancelButton = styled(Button)`
  && {
    background: ${WHITE};
    border: 1px solid ${GRAY_200};
    color: ${GRAY_700};
    border-radius: 8px;
    height: 44px;
    font-weight: ${fontWeights.semibold};
    padding: 10px 16px;

    &:hover {
      background: ${GRAY_50};
      border-color: ${GRAY_200};
      color: ${GRAY_700};
    }

    &:disabled {
      opacity: 0.7;
    }
  }
`;

const SendButton = styled(Button)`
  && {
    background: ${PRIMARY_600};
    border: 1px solid ${PRIMARY_600};
    color: ${WHITE};
    border-radius: 8px;
    height: 44px;
    font-weight: ${fontWeights.semibold};
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 10px 16px;

    &:hover {
      background: ${PRIMARY_800};
      border-color: ${PRIMARY_800};
    }

    &:disabled {
      background: ${GRAY_200};
      border-color: ${GRAY_200};
      color: ${GRAY_500};
    }
  }
`;

const getUrgencyBadge = (daysUntilExpiry) => {
  if (daysUntilExpiry <= 3) {
    return { bgColor: "#fef3f2", textColor: ERROR_500, label: "Urgent" };
  } else if (daysUntilExpiry <= 7) {
    return { bgColor: "#fffaeb", textColor: WARNING_700, label: "Soon" };
  }
  return { bgColor: "#ecfdf3", textColor: SUCCESS_700, label: "On Track" };
};

const ReviewPatientsModal = ({ visible, action, onClose, onSendSelected, isSending = false }) => {
  const { t } = useTranslation();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  // Initialize selected patients when action changes
  React.useEffect(() => {
    if (action?.patients) {
      const initiallySelected = action.patients.filter((p) => p.selected).map((p) => p.patientId);
      setSelectedRowKeys(initiallySelected);
    }
  }, [action]);

  const patients = action?.patients || [];

  const handleSend = useCallback(() => {
    const selectedPatients = action?.patients?.filter((p) => selectedRowKeys.includes(p.patientId));
    onSendSelected?.(selectedPatients, action);
  }, [selectedRowKeys, action, onSendSelected]);

  const getColumns = useCallback(() => {
    const baseColumns = [
      {
        title: t("pending_actions.table.patient", "Patient"),
        dataIndex: "firstName",
        key: "patient",
        render: (_, record) => {
          const patientPath = record.journeyId
            ? `/patient/${record.patientId}/journey/${record.journeyId}/optimizations`
            : `/patient/${record.patientId}`;

          return (
            <PatientName>
              <PatientLink to={patientPath} onClick={(e) => e.stopPropagation()}>
                {record.firstName} {record.lastName}
              </PatientLink>
              <PatientId>MRN {record.mrn || record.patientId}</PatientId>
            </PatientName>
          );
        }
      }
    ];

    // Add type-specific columns
    if (action?.type === ACTION_TYPES.NEW_PROGRAMS) {
      // For NEW_PROGRAMS, use programName for the Program column
      baseColumns.push(
        {
          title: t("pending_actions.table.program", "Program"),
          dataIndex: "programName",
          key: "programName",
          render: (name) => (
            <Tooltip title={name}>
              <Text style={{ maxWidth: 140 }} ellipsis>
                {name}
              </Text>
            </Tooltip>
          )
        },
        {
          title: t("pending_actions.table.program_type", "Type"),
          dataIndex: "programType",
          key: "programType",
          width: 80,
          render: (type) => (
            <Tag color={type === "Copay" ? "blue" : "green"} style={{ borderRadius: 4 }}>
              {type}
            </Tag>
          )
        }
      );
    } else {
      // For other types (M3P_ELIGIBLE, etc.), use assistanceProgramName for the Program column
      baseColumns.push({
        title: t("pending_actions.table.program", "Program"),
        dataIndex: "assistanceProgramName",
        key: "assistanceProgramName",
        render: (name) => <DrugTag>{name || "M3P"}</DrugTag>
      });
    }

    if (action?.type === ACTION_TYPES.PENDING_RENEWALS) {
      baseColumns.push(
        {
          title: t("pending_actions.table.renewal_date", "Renewal"),
          dataIndex: "renewalDate",
          key: "renewalDate",
          render: (date) => (
            <Text style={{ color: GRAY_700 }}>
              {new Date(date).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric"
              })}
            </Text>
          )
        },
        {
          title: t("pending_actions.table.status", "Status"),
          dataIndex: "daysUntilExpiry",
          key: "urgency",
          width: 100,
          render: (days) => {
            const { bgColor, textColor, label } = getUrgencyBadge(days);
            return (
              <UrgencyBadge bgColor={bgColor} textColor={textColor}>
                {days}d - {label}
              </UrgencyBadge>
            );
          }
        }
      );
    }

    return baseColumns;
  }, [action?.type, t]);

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys
  };

  const modalTitle = (
    <div>
      {action?.title}
      <ModalSubtitle>{t("pending_actions.modal.subtitle", "Review and select patients")}</ModalSubtitle>
    </div>
  );

  const modalFooter = (
    <>
      <FooterInfo>
        <span>
          {selectedRowKeys.length > 0
            ? `${selectedRowKeys.length} of ${patients.length} patient${selectedRowKeys.length > 1 ? "s" : ""} selected`
            : t("pending_actions.select_patients_to_proceed", "Select patients to proceed")}
        </span>
      </FooterInfo>
      <FooterActions>
        <CancelButton onClick={onClose} disabled={isSending}>
          {t("cancel", "Cancel")}
        </CancelButton>
        <SendButton
          type="primary"
          disabled={selectedRowKeys.length === 0 || isSending}
          onClick={handleSend}
          loading={isSending}
        >
          {!isSending && <SendOutlined />}
          {isSending
            ? t("pending_actions.sending", "Sending...")
            : `${action?.actionLabel} (${selectedRowKeys.length})`}
        </SendButton>
      </FooterActions>
    </>
  );

  return (
    <StyledModal
      open={visible}
      onCancel={onClose}
      title={modalTitle}
      footer={modalFooter}
      width={800}
      centered
      destroyOnClose
      maskClosable={false}
      closeIcon={<CloseOutlined style={{ color: GRAY_500 }} />}
    >
      <StyledTable
        rowSelection={rowSelection}
        columns={getColumns()}
        dataSource={patients}
        rowKey="patientId"
        size="small"
        pagination={{
          pageSize: 8,
          showSizeChanger: false,
          size: "small"
        }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t("pending_actions.no_patients_available", "No patients available")}
            />
          )
        }}
      />
    </StyledModal>
  );
};

export default ReviewPatientsModal;
